<template>
  <footer>
    <div class="w-full py-4 viewport-gutter">
      <div class="container flex justify-between">
        <div>
          <a
            v-if="!loading"
            href="#"
            class="inline-flex items-center text-sm underline transition-colors duration-200 ease-in-out cursor-pointer text-sn-secondary hover:no-underline hover:text-sn-secondary-700 xl:text-base"
            @click.prevent="showExitPopup = true"
          >
            <svg viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:class="'w-4 mr-2 fill-current'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M74 90.8l107-.2L50.6 221 79 249.3l130.4-130.4-.3 107.2 40.2-.1V50.7H74v40.1"/></svg>
            {{ $t('components.footer.returnToLH') }}
          </a>
        </div>

        <CancelRedemptionPopup v-model="showExitPopup" />

        <div>
          <div
            v-on-clickaway="closeLanguageSwitcher"
            class="relative lang-switch"
            :class="{ open: showLanguageSwitcher }"
          >
            <div
              class="inline-flex items-center justify-center text-sm leading-none cursor-pointer trigger text-sn-primary xl:text-base"
              @click="toggleLanguageSwitcher"
            >
              <div class="mr-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M10.5 15.638a17.429 17.429 0 013.23.4 13.141 13.141 0 01-3.075 3.926c-.052 0-.1.009-.155.012zm-1.181 4.325a13.13 13.13 0 01-3.075-3.926 17.342 17.342 0 013.23-.4v4.337c-.052-.002-.103-.008-.155-.011zm5.432-3.669A17.3 17.3 0 0117 17.11a9.983 9.983 0 01-4.592 2.579 14.133 14.133 0 002.343-3.395zm-11.78.817a17.308 17.308 0 012.253-.817 14.115 14.115 0 002.339 3.4 9.981 9.981 0 01-4.592-2.583zm12.2-1.761A14.287 14.287 0 0016.2 10.5h3.776a9.954 9.954 0 01-2.245 5.816 18.355 18.355 0 00-2.559-.966zM0 10.5h3.776A14.309 14.309 0 004.8 15.35a18.161 18.161 0 00-2.556.967A9.954 9.954 0 010 10.5zm10.5 4.111V10.5h4.671a13.26 13.26 0 01-1 4.585 18.4 18.4 0 00-3.671-.473zM4.8 10.5h4.674v4.111a18.41 18.41 0 00-3.67.474A13.234 13.234 0 014.8 10.5zm11.4-1.026a14.282 14.282 0 00-1.025-4.849 18.343 18.343 0 002.556-.967 9.959 9.959 0 012.246 5.816zm-5.7 0V5.362a18.335 18.335 0 003.669-.474 13.246 13.246 0 011 4.586zm-5.7 0a13.256 13.256 0 011-4.586 18.41 18.41 0 003.67.474v4.112zm-4.8 0a9.952 9.952 0 012.246-5.815 18.343 18.343 0 002.554.966 14.3 14.3 0 00-1.024 4.849zM10.5 0c.052 0 .1.008.155.012a13.137 13.137 0 013.075 3.926 17.277 17.277 0 01-3.23.4zM6.245 3.937A13.13 13.13 0 019.319.012c.051 0 .1-.009.155-.012v4.337a17.343 17.343 0 01-3.229-.4zm0 0zM2.971 2.864A10 10 0 017.563.284a14.133 14.133 0 00-2.339 3.4 17.177 17.177 0 01-2.253-.82zm9.441-2.58A9.991 9.991 0 0117 2.864a17.209 17.209 0 01-2.253.816A14.129 14.129 0 0012.412.284z" fill="#05164d"/></svg>
              </div>
              <span class="inline">{{ $t(`locales.${currentLocale}`) }}</span>
            </div>
            <div
              class="absolute bottom-0 right-0 hidden px-4 py-1 text-sm text-right bg-white dropdown mb-11 shadow-2 xl:text-base"
            >
              <ul>
                <li
                  v-for="(locale, i) in availableLocales"
                  :key="`Lang${i}`"
                  class="py-1 leading-none cursor-pointer xl:py-2"
                  @click.prevent="changeLocale(locale)"
                >
                  {{ $t(`locales.${locale}`) }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="px-4 py-8 text-white bg-sn-primary xl:py-6">
      <div class="container flex flex-col items-center justify-between space-y-5 xl:space-y-0 xl:flex-row">
        <nav class="w-full -mb-4">
          <ul class="flex justify-center text-sm xl:justify-start xl:text-base">
            <li class="pb-4 mr-8 leading-tight underline truncate hover:no-underline">
              <a :href="imprintLink" target="_blank">{{ $t('components.footer.links.imprint') }}</a>
            </li>
            <li class="pb-4 mr-8 leading-tight underline truncate hover:no-underline">
              <a :href="privacyLink" target="_blank">{{ $t('components.footer.links.privacy') }}</a>
            </li>
            <li class="pb-4 leading-tight underline truncate hover:no-underline">
              <a
                :href="`https://www.brusselsairlines.com/be/${termsOfUseLocale}/legal-notices/voucher-conditions`"
                target="_blank"
              >
                {{ $t("components.footer.links.termsOfUse") }}
              </a>
            </li>
          </ul>
        </nav>

        <div class="w-40 xl:w-64">
          <img src="~images/psp/star-alliance-white.svg" alt="Star Alliance Logo">
        </div>
      </div>
    </div>
  </footer>
</template>

<script lang="ts">
import { mixin as clickaway } from 'vue-clickaway'
import CancelRedemptionPopup from '~app/psp/brussels/cancel-redemption-popup.vue'
import Footer from '~app/psp/base/footer'

export default Footer.extend({
  components: {
    CancelRedemptionPopup,
  },
  mixins: [clickaway],

  data(): {
    showLanguageSwitcher: boolean;
    showExitPopup: boolean;
  } {
    return {
      showLanguageSwitcher: false,
      showExitPopup: false,
    }
  },
  /*
  mounted() {
    window.onbeforeunload = (event: Event) => {
      // TODO: every window.location or a href with outside link needs
      // to skip the code below in order to prevent the popup.
      // (since this is a wanted redirection compared to a page refresh or page close)
      // This can be done, so that for every link we dispatch an action,
      // that sets a state before actually doing the redirect.
      // Then in this function, if that variable is set to true
      // don't show a popup warning on leave.
      // event.preventDefault() // per the standard
      // event.returnValue = '' // required for Chrome
    }
  },
  */
  computed: {
    imprintLink(): string {
      switch (this.$i18n.locale) {
        case 'de':
          return 'https://www.brusselsairlines.com/be/de/legal-notices/imprint'
        case 'fr':
          return 'https://www.brusselsairlines.com/be/fr/legal-notices/imprint'
        case 'nl':
          return 'https://www.brusselsairlines.com/be/nl/legal-notices/imprint'
        default:
          return 'https://www.brusselsairlines.com/be/en/legal-notices/imprint'
      }
    },
    privacyLink(): string {
      switch (this.$i18n.locale) {
        case 'de':
          return 'https://www.brusselsairlines.com/be/de/legal-notices/data-protection-and-privacy-notice'
        case 'fr':
          return 'https://www.brusselsairlines.com/be/fr/legal-notices/data-protection-and-privacy-notice'
        case 'nl':
          return 'https://www.brusselsairlines.com/be/nl/legal-notices/data-protection-and-privacy-notice'
        default:
          return 'https://www.brusselsairlines.com/be/en/legal-notices/data-protection-and-privacy-notice'
      }
    },
  },
  methods: {
    toggleLanguageSwitcher() {
      this.showLanguageSwitcher = !this.showLanguageSwitcher
    },
    closeLanguageSwitcher() {
      this.showLanguageSwitcher = false
    },
    changeLocale(locale: string) {
      this.$store.dispatch('locale/set', { locale })
    },
  },
})
</script>

<style lang="scss" scoped>
.lang-switch {
  .dropdown {
    &::before {
      position: absolute;
      right: 1rem;
      bottom: -10px;
      width: 0;
      height: 0;
      content: '';
      -webkit-filter: drop-shadow(0 10px 0 rgba(0, 0, 0, 0.15));
      filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.15));
      border-color: #fff transparent transparent transparent;
      border-style: solid;
      border-width: 10px 15px 0 15px;
    }
    &::after {
      // triangel (before) shadow removal inside dropdown
      position: absolute;
      right: 1rem;
      bottom: 0;
      width: 30px;
      height: 5px;
      content: '';
      background: white;
    }
  }
  &.open {
    .dropdown {
      @apply block;

      display: block;
    }
  }
}
</style>
